import { StrictMode, useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { createRoot } from "react-dom/client";

import { createHttpTransport } from "@tdelivr/api-client";

import { arrivalFor, LeavingForTheProvider } from "./auth/arrival.tsx";
import { ConsoleAuthority, type SignInFailure } from "./auth/authority.ts";
import { isConfigured, readOidcConfiguration } from "./auth/configuration.ts";
import { useAuthentication } from "./auth/use-authentication.ts";
import { developmentSession } from "./development-session.ts";
import { dispatchConsolePages } from "./navigation.ts";
import { pageIdIn, urlForPage } from "./page-location.ts";
import type { Session } from "./session.ts";
import { ActiveDeliveries } from "./ui/ActiveDeliveries.tsx";
import { ActiveRides } from "./ui/ActiveRides.tsx";
import { Broadcasts } from "./ui/Broadcasts.tsx";
import { Departures } from "./ui/Departures.tsx";
import { SurgeZones } from "./ui/SurgeZones.tsx";
import { DemandQueue } from "./ui/DemandQueue.tsx";
import { DriverRoster } from "./ui/DriverRoster.tsx";
import { ExceptionsBoard } from "./ui/ExceptionsBoard.tsx";
import { Geofences } from "./ui/Geofences.tsx";
import { LiveMap } from "./ui/LiveMap.tsx";
import { OfferMonitor } from "./ui/OfferMonitor.tsx";
import { PageOutlook } from "./ui/PageOutlook.tsx";
import { ServiceAreas } from "./ui/ServiceAreas.tsx";
import { SessionEnded } from "./ui/SessionEnded.tsx";
import { Shell } from "./ui/Shell.tsx";
import { SignIn } from "./ui/SignIn.tsx";
import { SignOutDialog } from "./ui/SignOutDialog.tsx";
import { StateView } from "./ui/StateView.tsx";
import "./styles.css";

/**
 * The console, running.
 *
 * Two ways in, and only one of them exists in a production build.
 *
 * **Sign in.** OpenID Connect, authorization code flow with PKCE, against the provider named by
 * `VITE_OIDC_ISSUER`. The shape is not a choice: a browser application cannot hold a client secret,
 * so a public client with PKCE is the only correct option. `src/auth/` holds the whole of it and
 * `docs/adr/0018-the-console-signs-in-with-pkce.md` records why it is written out rather than
 * imported.
 *
 * An unauthenticated arrival does not draw a screen first. It goes straight to the provider's own
 * page, which is the sign-in page —
 * `docs/adr/0022-keycloaks-own-page-is-the-sign-in-page.md`, superseding ADR 0018 on that one point
 * and nothing else. `auth/arrival.tsx` holds the decision, including the two unauthenticated
 * arrivals that must be shown a page rather than redirected.
 *
 * **Development claims.** `VITE_ACCESS_TOKEN_CLAIMS`, read by `development-session.ts`, which is the
 * path the fidelity harness and the tests run on. Compiled out of a production build rather than
 * merely discouraged in it — see that file for the two mechanisms and the README for the measurement.
 */

/**
 * Tenant, then section, then page.
 *
 * The tenant is first for the reason the chrome states it at all: an operator who can cross tenants
 * must always know which side of the boundary they are on. The same three the shell's header is given,
 * because a page that drew different crumbs from its own chrome would be telling two stories.
 */
function crumbsFor(tenantName: string, pageId: string): readonly string[] {
  const page = dispatchConsolePages.find((candidate) => candidate.id === pageId);
  /*
   * The middle crumb is the **console**, not the page's navigation section.
   *
   * It was `page.section`, so every page in this console drew a trail the design does not: `EAM ›
   * In flight › Active deliveries` where the design draws `EAM › Operations › Active deliveries`.
   * Both design files that render a trail agree — `02 Console Shell v3` builds
   * `[tenant.short, C[1], …]` where `C[1]` is the console's own label from its `CONSOLES` table,
   * and `19 WP18 Dispatch Operations` writes `['EAM', 'Dispatch', …]` literally.
   *
   * `Operations` is the label, because that is what the table this trail reads from actually says:
   * `['ops', 'Operations', 'Dispatch, live operations, incidents', 'hub']`. `19`'s `Dispatch` is
   * the same console named in one word in a file about one console, and the trail's job is to say
   * which of the four consoles you are in — a distinction only the table makes.
   *
   * The section is not lost: it is what groups the page in the rail, which is where the design puts
   * it. A trail repeating it was giving the operator the same fact twice and the console's identity
   * not at all — the same duplication the breadcrumbs and the second top bar were both found doing.
   */
  return [tenantName, "Operations", page?.title ?? "Unknown page"];
}

interface ConsoleProps {
  readonly session: Session;
  /**
   * The bearer token for the next request, or undefined when there is no token to send.
   *
   * A function rather than a string, and asynchronous, because that is how a token stays fresh: the
   * authority refreshes one that is about to expire before handing it over, so a request never goes
   * out carrying a token a service will refuse. `createHttpTransport` already accepted a promise
   * here, so nothing in `packages/api-client` changed.
   *
   * Absent on the development path, which has no token at all. That is what the harness runs with
   * and what it has always run with: the stubs answer without looking at an authorization header.
   */
  readonly accessToken?: () => Promise<string | undefined>;
  readonly onSignOut?: () => void;
  /** Rendered in place of the page, with the chrome intact, as the design draws it. */
  readonly expired?: ReactNode;
}

function Console({ session, accessToken, onSignOut, expired }: ConsoleProps): ReactNode {
  /*
   * The open page, in the address bar.
   *
   * It was React state alone, which meant every load opened the live map and there was no way to say
   * which page an operator had asked for — including to the sign-in flow, which has to send them back
   * to it. `page-location.ts` is the whole of the mechanism.
   */
  const [pageId, setPageId] = useState<string>(() => pageIdIn(window.location.href));

  useEffect(() => {
    // Back and forward move between pages rather than out of the console, now that pages are
    // addressable. Without this the browser's back button leaves a single-page application that has
    // pushed history entries, which is the worst of both.
    const onPopState = (): void => {
      setPageId(pageIdIn(window.location.href));
    };
    window.addEventListener("popstate", onPopState);
    return (): void => {
      window.removeEventListener("popstate", onPopState);
    };
  }, []);

  const navigate = useCallback((next: string) => {
    window.history.pushState(null, "", urlForPage(window.location.href, next));
    setPageId(next);
  }, []);

  /*
   * One transport for the session, and memoised.
   *
   * The token and the tenant are read per request rather than captured, so a refreshed token is
   * picked up without rebuilding anything the screens hold. Memoised because the header's inbox
   * effect depends on the transport's identity, and the authentication state above this component
   * now changes — a fresh object on every render would reload the inbox on every render.
   */
  const transport = useMemo(
    () =>
      createHttpTransport({
        // The page's own origin, not "/". The transport resolves each path with `new URL(path, base)`,
        // and `new URL("v1/…", "/")` throws "Invalid base URL" because a bare slash is not an absolute
        // URL — so the default configuration could not make a single request, and every screen rendered
        // its error state. Found the first time the console was rendered by the fidelity check rather
        // than reasoned about, which is the argument for rendering it.
        //
        // Same origin is also the right default once the gateway exists: Document 17 WEB-0743 forbids
        // exposing the services directly, so the console and its API are one origin in every environment
        // that is not a developer's machine.
        baseUrl: import.meta.env.VITE_PLATFORM_URL ?? window.location.origin,
        tenantId: () => session.tenantId,
        ...(accessToken === undefined ? {} : { authorization: accessToken }),
      }),
    [session.tenantId, accessToken],
  );

  return (
    <Shell
      session={session}
      currentPageId={pageId}
      onNavigate={navigate}
      transport={transport}
      {...(onSignOut === undefined ? {} : { onSignOut })}
    >
      {expired !== undefined ? (
        expired
      ) : pageId === "live-map" ? (
        <LiveMap
          transport={transport}
          tenantId={session.tenantId}
          crumbs={crumbsFor(session.tenantName, pageId)}
        />
      ) : pageId === "demand-queue" ? (
        <DemandQueue
          transport={transport}
          tenantId={session.tenantId}
          crumbs={crumbsFor(session.tenantName, pageId)}
        />
      ) : pageId === "offer-monitor" ? (
        <OfferMonitor transport={transport} crumbs={crumbsFor(session.tenantName, pageId)} />
      ) : pageId === "exceptions" ? (
        <ExceptionsBoard
          transport={transport}
          tenantId={session.tenantId}
          crumbs={crumbsFor(session.tenantName, pageId)}
          scopes={session.scopes}
        />
      ) : pageId === "active-deliveries" ? (
        <ActiveDeliveries
          transport={transport}
          tenantId={session.tenantId}
          tenantName={session.tenantName}
          crumbs={crumbsFor(session.tenantName, pageId)}
          scopes={session.scopes}
        />
      ) : pageId === "broadcasts" ? (
        <Broadcasts
          transport={transport}
          tenantId={session.tenantId}
          crumbs={crumbsFor(session.tenantName, pageId)}
          scopes={session.scopes}
        />
      ) : pageId === "active-rides" ? (
        <ActiveRides
          transport={transport}
          tenantId={session.tenantId}
          crumbs={crumbsFor(session.tenantName, pageId)}
          scopes={session.scopes}
        />
      ) : pageId === "driver-roster" ? (
        <DriverRoster transport={transport} crumbs={crumbsFor(session.tenantName, pageId)} />
      ) : pageId === "service-areas" ? (
        <ServiceAreas
          transport={transport}
          crumbs={crumbsFor(session.tenantName, pageId)}
          scopes={session.scopes}
        />
      ) : pageId === "departures" ? (
        <Departures
          transport={transport}
          crumbs={crumbsFor(session.tenantName, pageId)}
          scopes={session.scopes}
        />
      ) : pageId === "surge" ? (
        <SurgeZones
          transport={transport}
          tenantId={session.tenantId}
          crumbs={crumbsFor(session.tenantName, pageId)}
          scopes={session.scopes}
        />
      ) : pageId === "geofences" ? (
        <Geofences
          transport={transport}
          tenantId={session.tenantId}
          crumbs={crumbsFor(session.tenantName, pageId)}
          scopes={session.scopes}
        />
      ) : (
        /*
         * Every one of this console's twelve pages is now a screen, so nothing routes here any more.
         *
         * Kept rather than deleted, and not as a precaution: `PageOutlook` is reached whenever an
         * address names a page this build does not draw — a stale bookmark, a hand-edited `?page=`,
         * or a page added to `navigation.ts` before its screen exists. It answers with what each
         * page is waiting on rather than a blank, which is the one useful thing a console can say
         * about a page it cannot open.
         */
        <PageOutlook session={session} />
      )}
    </Shell>
  );
}

/**
 * Everything above the console: leaving to sign in, the code exchange, the expiry and the sign-out.
 *
 * One switch over one state, which is the point of `ConsoleAuthority` holding all of it. The question
 * "is this operator signed in" has exactly one answer at any moment, and the chrome, the transport and
 * this switch all read it from the same place.
 *
 * Exactly one of its branches leaves this page: `signed_out`, and only when `arrivalFor` says the
 * arrival has nothing to report. Every other branch draws something. That asymmetry is the whole of
 * ADR 0022's caution — a state that is unauthenticated *because something went wrong* must be read,
 * not retried, or the console redirects into the same failure for as long as the browser lets it.
 */
function Authenticated({ authority }: { readonly authority: ConsoleAuthority }): ReactNode {
  const state = useAuthentication(authority);
  const [confirmingSignOut, setConfirmingSignOut] = useState(false);
  /**
   * Whether the operator has dismissed the sign-in screen's failure.
   *
   * The design's "Use a different account" returns to the start step. There is nothing to ask the
   * authority for — the failure is already final — so it is this component's state rather than the
   * authority's, which keeps the authority a statement about the session rather than about a screen.
   *
   * It returns to a page rather than to the provider on purpose. The operator has just been told why
   * a sign-in failed; the next move is theirs.
   */
  const [dismissedFailure, setDismissedFailure] = useState(false);

  const providerName = authority.providerName;
  const accessToken = useCallback(() => authority.accessToken(), [authority]);
  /*
   * Leave for the provider, from a button an operator pressed.
   *
   * No argument any more. It carried the email from the design's first step as `login_hint`, and ADR
   * 0022 removed that step: the provider's own form asks for the address, so collecting it here only
   * to send it there was the hop the operator decided against.
   */
  const signIn = useCallback(() => {
    setDismissedFailure(false);
    void authority.beginSignIn();
  }, [authority]);

  switch (state.kind) {
    case "starting":
      // The whole page, before anything has been decided. The design's loading state, which this
      // console renders in one place for every screen, centred because here it is the page.
      return (
        <div className="boot">
          <StateView state={{ kind: "loading" }} purpose="The console is starting." />
        </div>
      );

    case "signed_out":
      /*
       * The one render site that became a redirect, and the only one.
       *
       * Nobody is signed in and nothing has gone wrong, so there is nothing to say that the
       * provider's own page does not say better. `arrivalFor` decides, rather than this component
       * redirecting on sight, because two unauthenticated arrivals must still be given a page: the
       * operator who has just signed out, who would otherwise be sent back to the form they left,
       * and the tab that has already been to the provider and returned with no answer, which would
       * otherwise bounce between the two for ever. Both then land on the step below, which is the
       * design's first step with its email field removed.
       */
      return arrivalFor(state).kind === "to_the_provider" ? (
        <LeavingForTheProvider authority={authority} />
      ) : (
        <SignIn
          step="start"
          afterSignOut={state.afterSignOut}
          providerName={providerName}
          onContinue={signIn}
          onStartAgain={() => setDismissedFailure(true)}
        />
      );

    case "authenticating":
      // Left as a page, deliberately. This is the redirect in flight — discovery running, or the
      // code coming back to be exchanged — and it is what the operator sees for the moment before
      // the browser leaves. A redirect here would be a redirect during a redirect.
      return (
        <SignIn
          step="authenticating"
          afterSignOut={false}
          providerName={providerName}
          onContinue={signIn}
          onStartAgain={() => setDismissedFailure(true)}
        />
      );

    case "failed":
      /*
       * Left as a page, and this is the one that matters most.
       *
       * A sign-in that did not finish is exactly the state in which redirecting would loop: the
       * console would leave for the provider, fail the same way, and leave again, so an operator
       * whose account is not linked would never get to read the sentence telling them so. The
       * failure is drawn and the only way back out to the provider is a button they press.
       */
      return (
        <SignIn
          step={dismissedFailure ? "start" : "failed"}
          afterSignOut={false}
          providerName={providerName}
          failure={state.failure}
          onContinue={signIn}
          onStartAgain={() => setDismissedFailure(true)}
        />
      );

    case "expired":
      return (
        <Console
          session={state.session}
          accessToken={accessToken}
          expired={
            <SessionEnded
              tenantName={state.session.tenantName}
              endedAt={state.endedAt}
              lastedMs={state.lastedMs}
              onSignInAgain={() => {
                void authority.beginSignIn();
              }}
            />
          }
        />
      );

    case "signed_in":
      return (
        <>
          <Console
            session={state.session}
            accessToken={accessToken}
            onSignOut={() => setConfirmingSignOut(true)}
          />
          {confirmingSignOut ? (
            <SignOutDialog
              onDismiss={() => setConfirmingSignOut(false)}
              onConfirm={() => {
                setConfirmingSignOut(false);
                void authority.signOut();
              }}
            />
          ) : null}
        </>
      );
  }
}

/**
 * A build with no identity provider configured, said in the design's failure card.
 *
 * Also left as a page. There is no authorization endpoint to redirect to — that is what the card
 * says — so this is the state where a redirect is not merely a loop but impossible.
 */
function unconfigured(missing: readonly string[]): SignInFailure {
  return {
    code: "console_not_configured",
    headline: "This console has no identity provider configured",
    detail: `It was built without ${missing.join(" and ")}, so there is nowhere to send you to sign in. A platform administrator sets those when the console is built or served. Retrying won't change this.`,
    retryable: false,
  };
}

const root = document.getElementById("root");
if (root === null) throw new Error("The console has no mount point");

/*
 * Rendered synchronously, in every case.
 *
 * Not an aesthetic preference. Module scripts run before `DOMContentLoaded`, and the fidelity
 * harness asks whether a navigation button `isVisible()` immediately after that event without
 * waiting for it — so a bootstrap that awaited anything before its first render would have the
 * harness conclude the rail was absent and start opening a drawer that is not there at 1440. The
 * asynchronous part of signing in happens after the first paint, behind the design's loading state.
 */
const development = import.meta.env.DEV ? developmentSession() : undefined;

if (development !== undefined) {
  createRoot(root).render(
    <StrictMode>
      <Console session={development} />
    </StrictMode>,
  );
} else {
  const configuration = readOidcConfiguration(import.meta.env, window.location.origin);
  if (!isConfigured(configuration)) {
    createRoot(root).render(
      <StrictMode>
        <SignIn
          step="failed"
          afterSignOut={false}
          providerName="your organisation's sign-in"
          failure={unconfigured(configuration.missing)}
          onContinue={() => {}}
          onStartAgain={() => {}}
        />
      </StrictMode>,
    );
  } else {
    const authority = new ConsoleAuthority({
      configuration,
      fetch: globalThis.fetch.bind(globalThis),
      crypto: globalThis.crypto,
      // Per tab. Two tabs signing in at once must not read each other's code verifier, and a
      // verifier left in `localStorage` would outlive the browser session it belongs to.
      storage: window.sessionStorage,
      currentUrl: (): string => window.location.href,
      replaceUrl: (url: string): void => {
        window.history.replaceState(null, "", url);
      },
      navigate: (url: string): void => {
        window.location.assign(url);
      },
      now: (): number => Date.now(),
    });

    createRoot(root).render(
      <StrictMode>
        <Authenticated authority={authority} />
      </StrictMode>,
    );
    // After the first render, so the loading state is on screen while discovery and the code
    // exchange happen.
    void authority.start();
  }
}
